/**
 * Button — a big, soft, touch-first control. Works by TOUCH, mouse, AND keyboard
 * (Enter/Space when focused); never hover-only. Tap targets are at least
 * TOUCH_TARGET tall so a tired thumb never misses (cross-platform/touch HARD rule).
 *
 * Press feedback is a gentle scale + color shift (skipped under reduced motion).
 * Origin is centered to match the layout `place()` helpers.
 */
import Phaser from 'phaser';
import {
  COLORS,
  CSS,
  FONT,
  RADIUS,
  SPACING,
  TOUCH_SLOP,
  TOUCH_TARGET,
  isCoarsePointer,
} from './theme.js';
import { makeClickable } from './clickable.js';

export type ButtonVariant = 'primary' | 'ghost' | 'warm';

export interface ButtonOptions {
  readonly label: string;
  readonly onClick: () => void;
  readonly variant?: ButtonVariant;
  /** Fixed width; defaults to fit the label with comfortable padding. */
  readonly width?: number;
  readonly height?: number;
  readonly fontSize?: number;
  /** Disable motion feedback (mirrors Settings.reducedMotion). */
  readonly reducedMotion?: boolean;
}

interface VariantStyle {
  readonly fill: number;
  readonly fillPressed: number;
  readonly border: number;
  readonly text: string;
}

// Classic Gold/Silver buttons: a cream box with a crisp dark frame and dark ink, or
// the iconic blue "go" button with white text. Every variant keeps a dark G/S frame
// so it reads as a boxed control on the bright cream world (never light-on-light).
const VARIANTS: Record<ButtonVariant, VariantStyle> = {
  primary: {
    fill: COLORS.accent,
    fillPressed: COLORS.accentDeep,
    border: COLORS.boxBorder,
    // White on the G/S menu blue — the classic high-contrast primary.
    text: '#fbfaf2',
  },
  ghost: {
    fill: COLORS.surface,
    fillPressed: COLORS.surfaceAlt,
    border: COLORS.boxBorder,
    text: CSS.text,
  },
  // The amber Gold-version button — dark ink reads cleanly on the warm fill.
  warm: { fill: COLORS.warm, fillPressed: 0xc8902c, border: COLORS.boxBorder, text: CSS.boxText },
};

export class Button extends Phaser.GameObjects.Container {
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly labelText: Phaser.GameObjects.Text;
  private readonly style: VariantStyle;
  private readonly onClick: () => void;
  private readonly reducedMotion: boolean;
  private bWidth: number;
  private bHeight: number;
  private isDisabled = false;
  private pressed = false;

  constructor(scene: Phaser.Scene, x: number, y: number, options: ButtonOptions) {
    super(scene, x, y);
    this.style = VARIANTS[options.variant ?? 'primary'];
    this.onClick = options.onClick;
    this.reducedMotion = options.reducedMotion ?? false;

    const fontSize = options.fontSize ?? FONT.size.md;
    this.labelText = scene.add
      .text(0, 0, options.label, {
        fontFamily: FONT.family,
        fontSize: `${fontSize}px`,
        color: this.style.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.bWidth = options.width ?? Math.max(TOUCH_TARGET * 2, this.labelText.width + SPACING.xl);
    this.bHeight = options.height ?? Math.max(TOUCH_TARGET, fontSize + SPACING.lg);

    this.gfx = scene.add.graphics();
    this.add([this.gfx, this.labelText]);
    this.redraw();

    // The hit area must EXACTLY match the VISIBLE rect. The visual is drawn centered
    // (redraw() spans [-w/2..w/2, -h/2..h/2], label origin 0.5), so the hit area must
    // be the same centered rect. The previous code used Phaser's DEFAULT hit area
    // (setInteractive with no shape -> Rectangle(0,0,w,h), top-left anchored), which is
    // offset from the centered visual and left part of each button — including the Dex
    // "Back to Town" — unclickable. We pass an EXPLICIT centered shape instead, and
    // deliberately do NOT call setSize(): on a Phaser Container, setSize makes the
    // input displayOrigin = w/2 and re-introduces the very offset we are removing.
    // (This mirrors the working DexScene tiles: explicit centered rect, no setSize.)
    this.applyHitArea();

    this.on(Phaser.Input.Events.POINTER_DOWN, this.handleDown, this);
    this.on(Phaser.Input.Events.POINTER_UP, this.handleUp, this);
    this.on(Phaser.Input.Events.POINTER_OUT, this.handleOut, this);
    // Release outside the button cancels the press (per-object events don't fire
    // off the hit area; listen on the scene input plugin and clean up on shutdown).
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.handleGlobalUp, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      scene.input?.off(Phaser.Input.Events.POINTER_UP, this.handleGlobalUp, this);
    });

    scene.add.existing(this);
  }

  /**
   * Enable interactivity with a hit area that matches the visible (centered) rect.
   * Reuses the existing input.hitArea on resize so the clickable region tracks the
   * drawn button exactly — the single source of truth for all three call sites.
   */
  private applyHitArea(): void {
    // Centered hit rect + pointer cursor via the shared helper (single source of
    // truth across the whole UI kit). It reuses the persistent input.hitArea on
    // resize, so taps keep tracking the drawn button exactly. On a touch device the
    // hit rect is grown by a little TOUCH_SLOP per side (the VISUAL is unchanged) so a
    // fat thumb that lands just outside the drawn edge still registers — fixing the
    // "hitboxes feel small" report without ever shrinking below the visible button. On
    // a mouse desktop the slop is zero, so the hit area matches the visual exactly.
    const slop = isCoarsePointer() ? TOUCH_SLOP : 0;
    makeClickable(this, this.bWidth + slop * 2, this.bHeight + slop * 2);
  }

  /** Programmatic activation — also the keyboard (Enter/Space) entry point. */
  activate(): void {
    if (this.isDisabled) return;
    this.onClick();
  }

  setLabel(text: string): this {
    this.labelText.setText(text);
    return this;
  }

  /**
   * Resize the button's width in place (responsive layouts that fit a row/grid).
   * Re-draws the background and re-fits the centered hit area so taps stay accurate.
   * Width never drops below the touch-target minimum.
   */
  setWidth(width: number): this {
    const next = Math.max(TOUCH_TARGET, width);
    if (next === this.bWidth) return this;
    this.bWidth = next;
    // Resize the centered hit rect in place so taps keep matching the visible button.
    if (!this.isDisabled) this.applyHitArea();
    this.redraw();
    return this;
  }

  setDisabled(disabled: boolean): this {
    this.isDisabled = disabled;
    this.setAlpha(disabled ? 0.5 : 1);
    if (disabled) this.disableInteractive();
    else this.applyHitArea();
    return this;
  }

  get buttonWidth(): number {
    return this.bWidth;
  }
  get buttonHeight(): number {
    return this.bHeight;
  }

  private handleDown(): void {
    if (this.isDisabled) return;
    this.pressed = true;
    this.redraw();
    if (!this.reducedMotion) this.setScale(0.97);
  }

  private handleUp(): void {
    if (this.isDisabled) return;
    const wasPressed = this.pressed;
    this.handleOut();
    if (wasPressed) this.activate();
  }

  /** A pointerup anywhere: if it wasn't on us, cancel the in-progress press. */
  private handleGlobalUp(_pointer: Phaser.Input.Pointer): void {
    if (this.pressed) this.handleOut();
  }

  private handleOut(): void {
    this.pressed = false;
    this.redraw();
    if (!this.reducedMotion) this.setScale(1);
  }

  private redraw(): void {
    const w = this.bWidth;
    const h = this.bHeight;
    const r = Math.min(RADIUS.md, h / 2);
    const g = this.gfx;
    g.clear();
    g.fillStyle(this.pressed ? this.style.fillPressed : this.style.fill, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    g.lineStyle(1.5, this.style.border, 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
  }
}
